import { apiRequest, ApiError } from "@/lib/api/client";
import { getAuthToken } from "@/lib/api/token-store";

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || "").trim();
  if (!normalizedToken) {
    throw new ApiError("authorization token is required", 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

function jsonHeaders(token) {
  return {
    ...createAuthHeaders(token),
    "Content-Type": "application/json",
  };
}

function requireBlockId(blockId) {
  const id = String(blockId || "").trim();
  if (!id) {
    throw new ApiError("blockId is required", 400);
  }
  return id;
}

function requireDate(value, name) {
  const date = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    throw new ApiError(`${name} must be YYYY-MM-DD`, 400);
  }
  return date;
}

function appendMulti(params, key, values) {
  for (const value of Array.isArray(values) ? values : values ? [values] : []) {
    const trimmed = String(value || "").trim();
    if (trimmed) params.append(key, trimmed);
  }
}

/**
 * GET /api/admin/session-blocks — list session block documents.
 */
export async function fetchSessionBlocks({ token, tenantIds, doctorIds, calendarIds, label, limit, skip } = {}) {
  const params = new URLSearchParams();
  appendMulti(params, "tenantId", tenantIds);
  appendMulti(params, "doctorId", doctorIds);
  appendMulti(params, "calendarId", calendarIds);
  if (label) params.set("label", String(label).trim());
  if (Number.isFinite(limit) && limit > 0) params.set("limit", String(limit));
  if (Number.isFinite(skip) && skip > 0) params.set("skip", String(skip));
  const qs = params.toString();
  return apiRequest(`/api/admin/session-blocks${qs ? `?${qs}` : ""}`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
}

/**
 * GET /api/admin/session-blocks/{blockID} — canonical block document.
 */
export async function fetchSessionBlock({ token, blockId } = {}) {
  const id = requireBlockId(blockId);
  return apiRequest(`/api/admin/session-blocks/${encodeURIComponent(id)}`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
}

/**
 * POST /api/admin/session-blocks — create a block; the backend synchronously
 * materializes its child slots. Returns `{block, reconcile}`.
 */
export async function createSessionBlock({ token, body } = {}) {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    throw new ApiError("body is required", 400);
  }
  return apiRequest(`/api/admin/session-blocks`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(body),
  });
}

/**
 * PUT /api/admin/session-blocks/{blockID} — partial update + re-materialize.
 * Returns `{block, reconcile, warnings?}`.
 */
export async function updateSessionBlock({ token, blockId, body } = {}) {
  const id = requireBlockId(blockId);
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    throw new ApiError("body is required", 400);
  }
  return apiRequest(`/api/admin/session-blocks/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify(body),
  });
}

/**
 * DELETE /api/admin/session-blocks/{blockID} — soft-delete the block AND all
 * of its generated child slots (204).
 */
export async function deleteSessionBlock({ token, blockId } = {}) {
  const id = requireBlockId(blockId);
  return apiRequest(`/api/admin/session-blocks/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: createAuthHeaders(token),
  });
}

/**
 * GET /api/admin/session-blocks/occurrences — the who-is-where roster feed:
 * expanded per-date occurrences with overrides applied (suppressed ghosts for
 * cancelled/moved-out days, separate items for move targets).
 * `from`/`to` are inclusive YYYY-MM-DD dates.
 */
export async function fetchSessionOccurrences({ token, from, to, doctorIds, tenantIds, groupBy } = {}) {
  const params = new URLSearchParams();
  params.set("from", requireDate(from, "from"));
  params.set("to", requireDate(to, "to"));
  appendMulti(params, "doctorId", doctorIds);
  appendMulti(params, "tenantId", tenantIds);
  if (groupBy) params.set("groupBy", String(groupBy).trim());
  return apiRequest(`/api/admin/session-blocks/occurrences?${params.toString()}`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
}

/**
 * POST /api/admin/session-blocks/{blockID}:override — apply a per-date
 * exception (`cancel` | `move` | `relocate`).
 *
 * Conflict protocol: with the default `onConflict:"abort"` the backend answers
 * 409 + `{conflicts: [...]}` when booked appointments overlap the session and
 * writes nothing — that response IS the conflict-preview step. Resubmit with
 * `onConflict:"move"` (shift movable appointments along) or `"ignore"`.
 */
export async function applySessionOverride({ token, blockId, body } = {}) {
  const id = requireBlockId(blockId);
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    throw new ApiError("body is required", 400);
  }
  requireDate(body.date, "date");
  return apiRequest(`/api/admin/session-blocks/${encodeURIComponent(id)}:override`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(body),
  });
}

/**
 * POST /api/admin/session-blocks/{blockID}:revertOverride — remove one date's
 * override and restore the regular cadence. Auto-moved appointments are NOT
 * moved back; their ids are returned as `movedAppointmentIds`.
 */
export async function revertSessionOverride({ token, blockId, date } = {}) {
  const id = requireBlockId(blockId);
  return apiRequest(`/api/admin/session-blocks/${encodeURIComponent(id)}:revertOverride`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({ date: requireDate(date, "date") }),
  });
}

/**
 * POST /api/admin/session-blocks/{blockID}:regenerate — reconcile the block's
 * child slots. `dryRun:true` reports drift without writing.
 */
export async function regenerateSessionBlock({ token, blockId, dryRun = false } = {}) {
  const id = requireBlockId(blockId);
  return apiRequest(`/api/admin/session-blocks/${encodeURIComponent(id)}:regenerate`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({ dryRun: Boolean(dryRun) }),
  });
}
